// File: components/forms/patient/usePatientRegistrationSchema.js
import { useMemo } from 'react';
import { z } from 'zod';

import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import phoneValidation from '@/components/hooks/usePhoneValidator';
import { useCartData } from '../../context/CartDataContext';

const REQUIRED = 'Privalomas laukas';

// Trim helpers (kept private to validation module)
const trim = (v) => (typeof v === 'string' ? v.trim() : v);
const zTrimmedRequired = z.preprocess(trim, z.string().min(1, REQUIRED));
const zTrimmedOptional = z.preprocess(trim, z.string().optional());
const zTrimmedEmail = z
  .preprocess(
    trim,
    z.string().min(1, REQUIRED).email('Neteisingas el. pašto adresas'),
  )
  .refine((v) => /^[\u0000-\u007F]+$/.test(v), {
    message: 'Leidžiami tik lotyniški simboliai',
  });

export const makePatientRegistrationSchema = ({
  validatePersonalCode,
  maxAgeFromPersonalCode,
  validatePhoneNumber,
  service,
}) =>
  z
    .object({
      registerChild: z.boolean().default(false),

      patientFirstName: zTrimmedOptional,
      patientLastName: zTrimmedOptional,
      patientPersonalCode: zTrimmedOptional,

      payerFirstName: zTrimmedRequired,
      payerLastName: zTrimmedRequired,
      payerPhone: zTrimmedRequired,
      payerEmail: zTrimmedEmail,
      payerPersonalCode: zTrimmedRequired,
    })
    .superRefine((data, ctx) => {
      if (data.payerPhone) {
        const raw = data.payerPhone;
        if (!/^\+?\d+$/.test(raw)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['payerPhone'],
            message: 'Telefono numerio formatas netinkamas',
          });
        } else {
          const phone = validatePhoneNumber?.(raw);
          if (!phone || phone.valid !== true) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['payerPhone'],
              message: 'Netinkamas telefono numeris',
            });
          }
        }
      }

      if (data.payerPersonalCode) {
        if (!validatePersonalCode(data.payerPersonalCode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['payerPersonalCode'],
            message: 'Neteisingas asmens kodas',
          });
        } else if (!maxAgeFromPersonalCode(data.payerPersonalCode, 18)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['payerPersonalCode'],
            message: 'Asmuo nepilnametis',
          });
        }
      }

      if (data.registerChild) {
        if (!data.patientFirstName) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['patientFirstName'],
            message: REQUIRED,
          });
        }
        if (!data.patientLastName) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['patientLastName'],
            message: REQUIRED,
          });
        }
        if (!data.patientPersonalCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['patientPersonalCode'],
            message: REQUIRED,
          });
        } else {
          if (!validatePersonalCode(data.patientPersonalCode)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['patientPersonalCode'],
              message: 'Neteisingas asmens kodas',
            });
          } else {
            const pc = data.patientPersonalCode;

            if (
              service?.isChildrenAgeRestrictionApplicable &&
              service?.childAgeFrom
            ) {
              const tooOld = maxAgeFromPersonalCode(
                pc,
                service.childAgeTo ?? 18,
              );
              const tooYoung =
                service.childAgeFrom &&
                !maxAgeFromPersonalCode(pc, service.childAgeFrom);

              if (tooOld || tooYoung) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['patientPersonalCode'],
                  message: `Gydytojas konsultuoja tik vaikus nuo ${
                    service.childAgeFrom
                  } iki ${service.childAgeTo ?? 18} metų.`,
                });
              }
            } else {
              const isAdult = maxAgeFromPersonalCode(pc, 18);
              if (isAdult) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['patientPersonalCode'],
                  message: 'Įveskite vaiko iki 18 metų asmens kodą',
                });
              }
            }
          }
        }
      }
    });

export function usePatientRegistrationSchema() {
  const [validatePersonalCode, maxAgeFromPersonalCode] =
    personalCodeValidation();
  const validatePhoneNumber = phoneValidation();
  const { service } = useCartData();

  return useMemo(
    () =>
      makePatientRegistrationSchema({
        validatePersonalCode,
        maxAgeFromPersonalCode,
        validatePhoneNumber,
        service,
      }),
    [
      validatePersonalCode,
      maxAgeFromPersonalCode,
      validatePhoneNumber,
      service,
    ],
  );
}
